import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation } from 'react-router'
import { PageLoading } from '../app/PageLoading'
import { SignedInShell } from '../app/SignedInShell'
import { ButtonLink, EmptyState } from '../components'
import { useSession } from './useSession'

/** Signed-in screens: sends people to sign in (or to the PIN when locked), then to the join step if needed. */
export function RequireSession() {
  const { status, me, membership } = useSession()
  const location = useLocation()
  const next = encodeURIComponent(location.pathname + location.search)
  if (status === 'loading') return <PageLoading />
  if (status !== 'signed-in' || !me) return <Navigate to={`/signin?next=${next}`} replace />
  const consented = me.consents.includes('data_processing')
  // An organiser with no group yet may start one once she has given consent (O13 step 1).
  const startingGroup = location.pathname === '/app/start-group' && consented
  if (!startingGroup && (!consented || !membership)) return <Navigate to={`/join?next=${next}`} replace />
  return <SignedInShell />
}

/** /app opens on her role's home: Home for members, officer Home for officers in officer view. */
export function AppIndex() {
  const { mode } = useSession()
  return <Navigate to={mode === 'officer' ? '/app/officer' : '/app/home'} replace />
}

/**
 * Officer and console screens. An officer who arrives from a link while in member view
 * is switched to officer view so the navigation matches; anyone else is told plainly.
 */
export function RequireOfficer() {
  const { t } = useTranslation()
  const { membership, mode, setMode } = useSession()
  const officer = Boolean(membership?.is_officer)
  useEffect(() => {
    if (officer && mode !== 'officer') setMode('officer')
  }, [officer, mode, setMode])
  if (!officer) {
    return (
      <div className="container screen">
        <EmptyState icon="lock" title={t('shell.officerOnly.title')} body={t('shell.officerOnly.body')}
          action={<ButtonLink to="/app/home" variant="secondary">{t('shell.officerOnly.home')}</ButtonLink>} />
      </div>
    )
  }
  return <Outlet />
}
